'use client'

import { useEffect, useState } from 'react'
import { Tag, Sparkles } from 'lucide-react'
import { getStoredReferralCode } from '@/utils/referralStorage'
import { SignupProofBlock } from '@/components/client/landing/SignupProof'
import type { ProductWithPricing, DiscountCodeResponse } from '@sharpr/types'

interface PendingCheckout {
  productIds: string[]
  referralCode?: string
}

const TRIAL_DAYS = Number(process.env.NEXT_PUBLIC_TRIAL_DAYS || 5)
const TRIAL_LABEL = `${TRIAL_DAYS}-day free trial`

function readPendingCheckout(): PendingCheckout | null {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem('pending_checkout')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PendingCheckout> & { productId?: string }
    // Back-compat: older callers wrote `productId: string`. Normalize to productIds array.
    const productIds = Array.isArray(parsed.productIds)
      ? parsed.productIds
      : parsed.productId
        ? [parsed.productId]
        : []
    if (productIds.length === 0) return null
    return {
      productIds,
      referralCode: parsed.referralCode,
    }
  } catch {
    return null
  }
}

type AppliedCode = {
  codeType: 'referral' | 'promo'
  displayName: string
  discountLabel: string | null
}

export default function WelcomePanel() {
  const [hydrated, setHydrated] = useState(false)
  const [pending, setPending] = useState<PendingCheckout | null>(null)
  const [pendingProducts, setPendingProducts] = useState<ProductWithPricing[]>([])
  const [appliedCode, setAppliedCode] = useState<AppliedCode | null>(null)

  useEffect(() => {
    let cancelled = false

    const pendingCheckout = readPendingCheckout()
    const refCode = pendingCheckout?.referralCode ?? getStoredReferralCode()

    setPending(pendingCheckout)

    const productFetch: Promise<ProductWithPricing[]> = pendingCheckout
      ? fetch('/api/billing/products')
          .then((r) => (r.ok ? r.json() : null))
          .then((json) => {
            const products: ProductWithPricing[] = json?.data?.products ?? []
            return pendingCheckout.productIds
              .map((id) => products.find((p) => p.id === id))
              .filter((p): p is ProductWithPricing => p !== undefined)
          })
          .catch(() => [] as ProductWithPricing[])
      : Promise.resolve([] as ProductWithPricing[])

    const codeFetch: Promise<AppliedCode | null> = refCode
      ? fetch(`/api/billing/referral-code?code=${encodeURIComponent(refCode)}`)
          .then((r) => (r.ok ? (r.json() as Promise<DiscountCodeResponse>) : null))
          .then((json) =>
            json?.valid
              ? {
                  codeType: json.codeType,
                  displayName: json.displayName,
                  discountLabel: json.discount?.label ?? null,
                }
              : null
          )
          .catch(() => null)
      : Promise.resolve(null)

    Promise.all([productFetch, codeFetch]).then(([prods, code]) => {
      if (cancelled) return
      setPendingProducts(prods)
      setAppliedCode(code)
      setHydrated(true)
    })

    return () => {
      cancelled = true
    }
  }, [])

  const hasPlan = pending !== null
  const hasAppliedCode = appliedCode !== null

  // When multiple products were selected (bundle monthly), show "NBA + MLB" rather than a
  // single product name. Single-product checkout falls back to the product's own name.
  const displayPlanName =
    pendingProducts.length > 1
      ? pendingProducts.map((p) => p.name).join(' + ')
      : pendingProducts[0]?.name ?? null

  // Season-tail products replace the trial offer (rest of season free, first
  // charge at next season's resume) — the subheadline must not promise a trial.
  const tailLabel = (() => {
    const tail = pendingProducts.filter((p) => p.season_tail)
    return tail.length > 0 ? tail.map((p) => p.name).join(' + ') : null
  })()

  const { headline, subheadline } = resolveCopy({
    hydrated,
    hasPlan,
    productName: displayPlanName,
    tailLabel,
    appliedCode,
  })

  return (
    <aside
      aria-label="What you're signing up for"
      className={`relative ${hasPlan || hasAppliedCode ? 'flex px-5 pt-4 pb-2' : 'hidden'} md:flex flex-col justify-center md:py-16 md:pr-10 lg:pr-16`}
    >
      <div className="relative w-full max-w-md mx-auto md:mx-0">
        {/* Headline — desktop only; mobile form card carries the branded H1 to avoid
            duplicating the hero when the welcome panel is shown for pending-plan chips. */}
        <h1 className="hidden md:block text-4xl lg:text-5xl font-semibold tracking-tight text-balance text-fg dark:text-text-primary">
          {headline}
        </h1>

        {/* Subheadline (desktop only — mobile form card carries the context) */}
        <p className="hidden md:block mt-4 text-base md:text-[17px] leading-snug text-balance text-fg/70 dark:text-text-secondary/90">
          {subheadline}
        </p>

        {/* Context chips */}
        {(hasPlan || hasAppliedCode) && (
          <div className="mt-3 md:mt-5 flex flex-wrap gap-2 justify-center md:justify-start">
            {hasPlan && pendingProducts.length > 0 && pendingProducts.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/30 text-sm font-medium"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {p.name}
              </span>
            ))}
            {hasPlan && pendingProducts.length === 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/30 text-sm font-medium">
                <Sparkles className="w-3.5 h-3.5" />
                Plan selected
              </span>
            )}
            {hasAppliedCode && appliedCode && (
              <span className="inline-flex flex-col items-start gap-0.5 px-3 py-1.5 rounded-xl bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30 text-sm font-medium">
                <span className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  {appliedCode.codeType === 'referral'
                    ? `Referred by ${appliedCode.displayName}`
                    : `Code ${appliedCode.displayName} applied`}
                </span>
                {appliedCode.discountLabel && (
                  <span className="text-xs pl-5">{appliedCode.discountLabel}</span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Proof (desktop only). */}
        <div className="hidden md:block mt-10">
          <SignupProofBlock />
        </div>
      </div>
    </aside>
  )
}

function resolveCopy({
  hydrated,
  hasPlan,
  productName,
  tailLabel,
  appliedCode,
}: {
  hydrated: boolean
  hasPlan: boolean
  productName: string | null
  tailLabel: string | null
  appliedCode: AppliedCode | null
}): { headline: React.ReactNode; subheadline: string } {
  const referralName = appliedCode?.codeType === 'referral' ? appliedCode.displayName : null
  const promoLabel =
    appliedCode?.codeType === 'promo'
      ? appliedCode.discountLabel
        ? `${appliedCode.displayName} (${appliedCode.discountLabel})`
        : appliedCode.displayName
      : null

  // Cold state (also pre-hydration default — avoids hydration mismatch)
  if (!hydrated || (!hasPlan && !appliedCode)) {
    return {
      headline: (
        <>
          Find the{' '}
          <img
            src="/sharpr.png"
            alt="Sharpr"
            className="inline-block h-8 md:h-12 w-auto align-[-0.2em]"
          />{' '}
          edge
        </>
      ),
      subheadline: 'Projections, research, and live +EV edges for every NBA and MLB player prop.',
    }
  }

  const planLabel = productName ?? 'your plan'

  if (tailLabel) {
    const suffix = referralName
      ? ` — sent by ${referralName}.`
      : promoLabel
        ? ` — code ${promoLabel} applied.`
        : '.'
    return {
      headline: "You're one step from the edge",
      subheadline: `Create your account — ${tailLabel} is free for the rest of this season${suffix}`,
    }
  }

  if (hasPlan && referralName) {
    return {
      headline: "You're one step from the edge",
      subheadline: `Create your account to start your ${TRIAL_LABEL} of ${planLabel} — sent by ${referralName}.`,
    }
  }

  if (hasPlan && promoLabel) {
    return {
      headline: "You're one step from the edge",
      subheadline: `Create your account to start your ${TRIAL_LABEL} of ${planLabel} — code ${promoLabel} applied.`,
    }
  }

  if (hasPlan) {
    return {
      headline: "You're one step from the edge",
      subheadline: productName
        ? `Create your account to start your ${TRIAL_LABEL} of ${planLabel}.`
        : `Create your account to start your ${TRIAL_LABEL}.`,
    }
  }

  // Code-only states
  if (referralName) {
    return {
      headline: `Welcome — sent here by ${referralName}`,
      subheadline: `Create your account to start your ${TRIAL_LABEL}.`,
    }
  }

  return {
    headline: 'Welcome to Sharpr',
    subheadline: promoLabel
      ? `Code ${promoLabel} applied. Create your account to start your ${TRIAL_LABEL}.`
      : `Create your account to start your ${TRIAL_LABEL}.`,
  }
}
